import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Stores from "@/pages/Stores";
import StoreForm from "@/pages/StoreForm";
import Categories from "@/pages/Categories";
import Products from "@/pages/Products";
import ProductForm from "@/pages/ProductForm";
import ProductVariants from "@/pages/ProductVariants";
import Orders from "@/pages/Orders";
import OrderDetails from "@/pages/OrderDetails";
import Payments from "@/pages/Payments";
import WalletDues from "@/pages/WalletDues";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import DeliverySettings from "@/pages/DeliverySettings";
import Banners from "@/pages/Banners";

// Layout
import AdminLayout from "@/components/AdminLayout";

// Auth context
import { AuthProvider, useAuth } from "@/context/AuthContext";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center animate-pulse"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
          />
          <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-700 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-700 rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />

          <Route path="/" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="stores" element={<Stores />} />
            <Route path="stores/new" element={<StoreForm />} />
            <Route path="stores/:id/edit" element={<StoreForm />} />
            <Route path="categories" element={<Categories />} />
            <Route path="products" element={<Products />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:id/edit" element={<ProductForm />} />
            <Route path="products/:id/variants" element={<ProductVariants />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetails />} />
            <Route path="payments" element={<Payments />} />
            <Route path="wallet-dues" element={<WalletDues />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
            <Route path="delivery-settings" element={<DeliverySettings />} />
            <Route path="banners" element={<Banners />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster
        position="bottom-right"
        richColors
        toastOptions={{
          style: { borderRadius: '14px', fontFamily: 'Public Sans, sans-serif' },
        }}
      />
    </AuthProvider>
  );
}

export default App;
